import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
} from '@angular/core';

import { FOOTER_HEIGHT } from '@shared/constants/layout.constants';

@Directive({
  selector: '[auxStickyFixedHeightGrid]',
  standalone: true,
})
/**
 * Directive to make an element sticky with fixed height.
 * The element that needs to be sticky should be wrapped in a div element.
 * Example:
 * ```html
 * <div auxStickyFixedHeightGrid>
 *   <ag-grid-angular />
 * </div>
 * ```
 */
export class StickyFixedHeightGridDirective implements AfterViewInit {
  private elementRef = inject(ElementRef);

  offset = input(0);

  sticked = output<number>();

  private headerIsSticking = false;
  private tableWidth = 0;

  ngAfterViewInit(): void {
    this.reassignElementWidthAndHeight();
  }

  private getContainerWidth(): number {
    return this.getSpacerWidth() || this.tableWidth;
  }

  private getSpacerWidth(): number {
    const spacer = document.getElementById('sticky-spacer');

    return spacer ? spacer.offsetWidth : 0;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.reassignElementWidthAndHeight();
    this.stickElement();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.reassignElementWidthAndHeight();
    this.stickElement();
  }

  private reassignElementWidthAndHeight() {
    this.tableWidth = this.elementRef.nativeElement.offsetWidth;

    if (this.headerIsSticking) {
      const containerWidth = this.getContainerWidth();

      this.elementRef.nativeElement.style.width = `${containerWidth}px`;
    }
  }

  private stickElement() {
    const elementRect = this.elementRef.nativeElement.getBoundingClientRect();
    const spacer = document.getElementById('sticky-spacer');

    if (!this.headerIsSticking) {
      const elementDistanceFromTop = elementRect.top;

      if (elementDistanceFromTop <= 0) {
        this.stickHeader();
      }
    } else {
      // When header is sticky, check spacer position instead
      const spacerRect = spacer?.getBoundingClientRect();

      if (spacerRect && spacerRect.top > 0) {
        this.unstickHeader();
      }
    }
  }

  private stickHeader(): void {
    const element = this.elementRef.nativeElement;

    const viewportHeight = window.innerHeight;
    const topOffset = 0; // Adjust if you have header above
    const bottomPadding = FOOTER_HEIGHT + this.offset(); // Adjust for bottom spacing
    const containerHeight = viewportHeight - topOffset - bottomPadding;

    const containerWidth = this.getContainerWidth();

    element.style.position = 'fixed';
    element.style.top = '0';
    element.style.width = `${containerWidth}px`;
    element.style.height = `${containerHeight}px`;
    element.style.zIndex = '1000';

    // Add spacer div to prevent content jump
    const spacer = document.createElement('div');

    spacer.style.width = `100%`;
    spacer.style.height = `${containerHeight}px`;
    spacer.id = 'sticky-spacer';
    element.parentNode.insertBefore(spacer, element);

    this.headerIsSticking = true;
    this.sticked.emit(containerHeight);
  }

  private unstickHeader(): void {
    const element = this.elementRef.nativeElement;

    element.style.position = '';
    element.style.top = '';
    element.style.width = '';
    element.style.height = '';
    element.style.zIndex = '';

    // Remove spacer div
    const spacer = document.getElementById('sticky-spacer');

    if (spacer) {
      spacer.remove();
    }

    this.headerIsSticking = false;
  }
}
